import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';

import { e2eTestsSkipToken } from '@/constants/env';
import { TelecomService } from '@/services/telecom.service';
import type { AuthData, AuthTokenType, User } from '@/types';
import { AdditiveUserRoles, UserRole } from '@/types';
// Imported by subpath, not from the package barrel: the barrel drags in the Amazon Connect/Chime
// component graph, and almost every pms test file reaches this module.
import { TelecomService as ClinicTelecomService } from '@montugroup/clinic-telecom/telecom.service';

export type Role = 'superAdmin' | 'admin' | 'adminOnly' | 'nurse' | 'doctor' | 'manager' | 'ptxSupportPartner';

export const isAuthenticated = () => {
  const access = localStorage.getItem('token') ?? null;
  const decoded: AuthTokenType | null = access ? jwtDecode(access) : null;
  // add this GHA variable for the e2e tests to skip token check
  const overrideJWTToken = e2eTestsSkipToken() === 'true';

  // TODO need to find better way to update the token expiry
  if (overrideJWTToken) {
    return !!access;
  }

  return decoded && decoded.exp ? DateTime.fromMillis(decoded.exp * 1000) > DateTime.now() : false;
};

export const getUser = (): User | null => {
  if (!isAuthenticated()) {
    return null;
  }

  const raw = localStorage.getItem('user');
  if (!raw) {
    return null;
  }

  const stored = JSON.parse(raw);

  // Fallback only if actor_type is missing
  if (stored.actor_type === undefined && stored.role_id !== undefined) {
    stored.actor_type = stored.role_id;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { role_id, ...normalized } = stored;
  localStorage.setItem('user', JSON.stringify(normalized));

  return normalized;
};

export const getToken = () => (isAuthenticated() ? localStorage.getItem('token') : null);

export const getUserRoleIds = (): number[] => {
  const u = getUser();
  if (!u) {
    return [];
  }

  const grantedRoleIds = u.roles?.map((r: { id: number }) => r.id) ?? [];

  const allGrantedRolesAreAdditive = grantedRoleIds.every((id) => AdditiveUserRoles[id as UserRole] ?? false);

  const roleIds = allGrantedRolesAreAdditive && u.actor_type ? [u.actor_type, ...grantedRoleIds] : grantedRoleIds;

  return Array.from(new Set(roleIds));
};

export const isAdmin = () =>
  isAuthenticated()
    ? getUserRoleIds().some((rid) => [UserRole.SuperAdmin, UserRole.Admin, UserRole.PtxSupportPartner].includes(rid))
    : false;

export const isAdminOnly = () =>
  isAuthenticated()
    ? Array.from(new Set(getUserRoleIds())).length === 1 && Array.from(new Set(getUserRoleIds()))[0] === UserRole.Admin
    : false;

export const isSuperAdmin = () => (isAuthenticated() ? getUserRoleIds().includes(UserRole.SuperAdmin) : false);

export const isPtxSupportPartner = () =>
  isAuthenticated() ? getUserRoleIds().includes(UserRole.PtxSupportPartner) : false;

export const isPrivacyModerator = () =>
  isAuthenticated() ? getUserRoleIds().includes(UserRole.PrivacyModerator) : false;

export const isNurse = () => (isAuthenticated() ? getUserRoleIds().includes(UserRole.Nurse) : false);

export const isDoctor = () => (isAuthenticated() ? getUserRoleIds().includes(UserRole.Doctor) : false);

export const isManager = () => (isAuthenticated() ? getUserRoleIds().includes(UserRole.Manager) : false);

export const userHasAnyRole = (roles: UserRole[]) => {
  if (!isAuthenticated()) {
    return false;
  }

  const userRoles = getUserRoleIds();
  return roles.some((role) => userRoles.includes(role));
};

export const getAuthData = (): AuthData => {
  if (isAuthenticated()) {
    return {
      user: getUser(),
      isAdmin: isAdmin(),
      token: localStorage.getItem('token'),
      isNurse: isNurse(),
      isDoc: isDoctor(),
      isSuperAdmin: isSuperAdmin(),
      isAdminOnly: isAdminOnly(),
      isManager: isManager(),
      isPtxSupportPartner: isPtxSupportPartner(),
      isPrivacyModerator: isPrivacyModerator(),
    };
  }
  return {
    user: null,
    isAdmin: false,
    token: null,
    isNurse: false,
    isDoc: false,
    isSuperAdmin: false,
    isAdminOnly: false,
    isManager: false,
    isPtxSupportPartner: false,
    isPrivacyModerator: false,
  };
};

export const login = (payload: { user: User; token: string }) => {
  localStorage.setItem('user', JSON.stringify(payload.user));
  localStorage.setItem('token', payload.token);
  return payload.user;
};

export const logout = () => {
  window.location.href = '/?expired';
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  TelecomService.clearWaitingRoomQuery();
  const spotConsultationIds = ClinicTelecomService.getAllSpotConsultationContactIds();
  spotConsultationIds.forEach((id) => {
    ClinicTelecomService.clearSpotConsultationContactId(id);
  });
  const redirectPatientIds = ClinicTelecomService.getAllRedirectPatientContactIds();
  redirectPatientIds.forEach((id) => {
    ClinicTelecomService.clearRedirectPatientContactId(id);
  });
};
